import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { API, errMsg } from "../../lib/api";
import { fmtDate } from "../../lib/format";
import { PageHeader, Loading, StatusBadge, Modal, Field, Table, Empty } from "../../components/common";

export default function AdminWorkQueue() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", type: "GENERAL", priority: "MEDIUM", assigned_to: "", due_date: "", notes: "" });

  const load = () => {
    const q = filter ? `?status=${filter}` : "";
    API.get(`/admin/tasks${q}`).then((r) => setData(r.data));
  };
  useEffect(() => { load(); }, [filter]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await API.post("/admin/tasks", { ...form, assigned_to: form.assigned_to || null });
      toast.success("Task created");
      setOpen(false); setForm({ title: "", type: "GENERAL", priority: "MEDIUM", assigned_to: "", due_date: "", notes: "" });
      load();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const patch = async (id, update) => {
    try { await API.patch(`/admin/tasks/${id}`, update); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <Loading label="Loading work queue" />;

  return (
    <div data-testid="admin-work-queue">
      <PageHeader title="Work Queue" sub="Centralized tasks for the whole team — returns, documents, payments, rescue."
        right={<button className="btn-navy" onClick={() => setOpen(true)} data-testid="new-task-btn"><Plus className="h-4 w-4" /> New Task</button>} />

      <div className="mb-4 flex gap-1 rounded-md border border-slate-200 bg-white p-1 w-fit">
        {["", "OPEN", "IN_PROGRESS", "DONE"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`task-filter-${s || "all"}`}
            className={`rounded px-3 py-1.5 text-sm font-semibold ${filter === s ? "bg-navy text-white" : "text-slate-600"}`}>{s || "All"}</button>
        ))}
      </div>

      {data.tasks.length === 0 ? <Empty title="Queue is clear" /> : (
        <Table headers={["Task", "Type", "Priority", "Due", "Assigned", "Status", "Actions"]} testid="tasks-table">
          {data.tasks.map((t) => (
            <tr key={t.id} className="border-b border-slate-100" data-testid={`task-${t.id}`}>
              <td className="px-4 py-2.5"><div className="font-medium text-slate-800">{t.title}</div>{t.notes && <div className="text-xs text-slate-400">{t.notes}</div>}</td>
              <td className="px-4 py-2.5 text-xs font-semibold text-slate-500">{t.type.replace(/_/g, " ")}</td>
              <td className="px-4 py-2.5"><StatusBadge status={t.priority === "HIGH" ? "PAST_DUE" : t.priority === "MEDIUM" ? "UPCOMING" : "DRAFT"} /></td>
              <td className="px-4 py-2.5 text-sm">{t.due_date ? fmtDate(t.due_date) : "—"}</td>
              <td className="px-4 py-2.5">
                <select className="rounded border border-slate-300 px-2 py-1 text-xs" value={t.assigned_to || ""} onChange={(e) => patch(t.id, { assigned_to: e.target.value || null })} data-testid={`assign-task-${t.id}`}>
                  <option value="">Unassigned</option>
                  {data.staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </td>
              <td className="px-4 py-2.5"><StatusBadge status={t.status} /></td>
              <td className="px-4 py-2.5">
                <div className="flex gap-1">
                  {t.status === "OPEN" && <button className="rounded border border-sky-300 px-2 py-1 text-xs font-semibold text-sky-700" onClick={() => patch(t.id, { status: "IN_PROGRESS" })} data-testid={`start-task-${t.id}`}>Start</button>}
                  {t.status !== "DONE" && <button className="rounded border border-emerald-300 px-2 py-1 text-xs font-semibold text-emerald-700" onClick={() => patch(t.id, { status: "DONE" })} data-testid={`done-task-${t.id}`}>Done</button>}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New Task">
        <form onSubmit={create} className="space-y-3" data-testid="task-form">
          <Field label="Title *"><input className="input-gov" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="task-title" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <select className="input-gov" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="task-type">
                {["GENERAL", "RETURN_REVIEW", "MISSING_DOCS", "FAILED_PAYMENT", "TAX_RESCUE", "FOLLOW_UP"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Priority">
              <select className="input-gov" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="task-priority">
                {["LOW", "MEDIUM", "HIGH"].map((p) => <option key={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Due Date"><input type="date" className="input-gov" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-due" /></Field>
            <Field label="Assign To">
              <select className="input-gov" value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} data-testid="task-assign">
                <option value="">Unassigned</option>
                {data.staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Notes"><textarea className="input-gov" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="task-notes" /></Field>
          <button className="btn-navy w-full" data-testid="task-submit">Create Task</button>
        </form>
      </Modal>
    </div>
  );
}
